import React, { useEffect, useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { TaskList } from './components/TaskList';
import { TaskDetail } from './components/TaskDetail';
import { NewTaskModal } from './components/NewTaskModal';
import { useTaskStore } from './store/taskStore';
import { Crosshair, Clock, AlertCircle, CalendarDays, Plus, Brain } from 'lucide-react';
import { Auth } from './components/Auth';
import { supabase } from './lib/supabase';

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'urgent' | 'this-week' | 'upcoming'>('all');
  
  const { tasks, analyzeAndUpdateTasks } = useTaskStore();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      const interval = setInterval(analyzeAndUpdateTasks, 30 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [analyzeAndUpdateTasks, session]);

  if (!session) {
    return <Auth />;
  }

  const filteredTasks = tasks.filter(task => {
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    const weekFromNow = new Date(today);
    weekFromNow.setDate(today.getDate() + 7);
    
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    switch (activeTab) {
      case 'urgent':
        return task.priority === 'urgent';
      case 'this-week':
        return dueDate >= startOfWeek && dueDate <= endOfWeek;
      case 'upcoming':
        return dueDate <= weekFromNow && dueDate >= today;
      default:
        return true;
    }
  });

  return (
    <div className="min-h-screen cyber-bg text-gray-300">
      <div className="cyber-content max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Brain className="w-10 h-10 text-[#00fff9] animate-pulse" />
              <div className="absolute inset-0 w-10 h-10 border-2 border-[#00fff9] rounded-full opacity-50 animate-ping" />
              <div className="absolute inset-0 w-10 h-10 border border-[#ff00c1] rounded-full opacity-30" />
            </div>
            <h1 className="text-3xl font-bold cyberpunk-title text-[#00fff9]">NEURO</h1>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowNewTaskModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#00fff933] text-[#00fff9] rounded-lg font-mono hover:bg-[#00fff966] transition-colors"
            >
              <Plus className="w-5 h-5" />
              NEW_MISSION
            </button>
            <button
              onClick={() => supabase.auth.signOut()}
              className="px-4 py-2 bg-[#ff00c133] text-[#ff00c1] rounded-lg font-mono hover:bg-[#ff00c166] transition-colors"
            >
              DISCONNECT
            </button>
          </div>
        </div>

        <div className="cyberpunk-card rounded-lg p-6 mb-6 neon-border">
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === 'all'
                  ? 'bg-[#00fff9] text-black font-bold'
                  : 'hover:bg-[#00fff933] text-[#00fff9]'
              }`}
            >
              <Crosshair className="w-5 h-5" />
              ALL_MISSIONS
            </button>
            <button
              onClick={() => setActiveTab('urgent')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === 'urgent'
                  ? 'bg-[#ff00c1] text-black font-bold'
                  : 'hover:bg-[#ff00c133] text-[#ff00c1]'
              }`}
            >
              <AlertCircle className="w-5 h-5" />
              PRIORITY_OPS
            </button>
            <button
              onClick={() => setActiveTab('this-week')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === 'this-week'
                  ? 'bg-[#7c00ff] text-white font-bold'
                  : 'hover:bg-[#7c00ff33] text-[#7c00ff]'
              }`}
            >
              <CalendarDays className="w-5 h-5" />
              THIS_WEEK
            </button>
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === 'upcoming'
                  ? 'bg-[#f7d000] text-black font-bold'
                  : 'hover:bg-[#f7d00033] text-[#f7d000]'
              }`}
            >
              <Clock className="w-5 h-5" />
              UPCOMING_JOBS
            </button>
          </div>
        </div>

        <TaskList
          tasks={filteredTasks}
          onTaskClick={setSelectedTask}
        />

        {selectedTask && (
          <TaskDetail
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
          />
        )}

        {showNewTaskModal && (
          <NewTaskModal onClose={() => setShowNewTaskModal(false)} />
        )}
      </div>
    </div>
  );
}

export default App;